import React, { lazy, Suspense, useCallback, useEffect } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { useGlobal } from 'context/global/GlobalContext';
import { theme } from 'styles/theme';
import './style.css';
import {
  CornerStyle,
  useThemeContext,
} from 'context/ThemeContext/ThemeContext';
import useLastLocation from 'hooks/useLastLocation';
import handleTranslation from 'utils/handleTranslation';
import ProtectedRoute from 'components/routing/ProtectedRoute';
import ProductDetails from 'pages/ProductDetails/ProductDetails';

const Login = lazy(() => import('pages/Login/Login'));
const FourZeroFour = lazy(() => import('pages/404/404'));
const MarkAsLost = lazy(() => import('pages/MarkAsLost'));
const Profile = lazy(() => import('pages/Profile/Profile'));
const Landing = lazy(() => import('pages/Landing/Landing'));
const LostAndFound = lazy(() => import('pages/LostAndFound'));
const MagicLink = lazy(() => import('pages/MagicLink/MagicLink'));
const GameRules = lazy(() => import('pages/GameRules/GameRules'));
const Collection = lazy(() => import('pages/Collection/Collection'));
const TermsAndPrivacy = lazy(
  () => import('pages/TermsAndPrivacy/TermsAndPrivacy')
);

interface RoutesType {
  [key: string]: any;
}

export const RoutesHashMap: RoutesType = {
  Login: {
    path: '/app/login',
    component: <Login />,
    exact: true,
  },
  MagicLink: {
    path: '/app/magic-link',
    component: <MagicLink />,
    exact: true,
  },
  FourZeroFour: {
    path: '/app/404',
    component: <FourZeroFour />,
    exact: true,
  },
  Collection: {
    path: '/app/collection',
    component: <Collection />,
    protected: true,
    exact: true,
  },
  Profile: {
    path: '/app/profile',
    component: <Profile />,
    protected: true,
    exact: true,
  },
  LostAndFound: {
    path: '/app/lost-and-found',
    component: <LostAndFound />,
    protected: false,
    exact: true,
  },
  MarkAsLost: {
    path: '/app/mark-as-lost',
    component: <MarkAsLost />,
    protected: true,
    exact: true,
  },
  Terms: {
    path: (id: string = `:id`) => `/p/${id}`,
    component: <TermsAndPrivacy />,
    exact: true,
  },
  GameRules: {
    path: (id: string = `:id`) => `/s/${id}`,
    component: <GameRules />,
    exact: true,
  },
  ProductDetails: {
    path: (id: string = ':id') => `/c/${id}`,
    component: <ProductDetails />,
    exact: false,
  },
  Landing: {
    path: '/app/l/:brandname',
    component: <Landing />,
    exact: true,
  },
};

const getTransition = (path: string, lastLocation: string) => {
  if (path.startsWith('/p/') || path.startsWith('/s/')) return 'inverseslide';
  switch (path) {
    case '/app/login':
      return 'inverseslide';
    case '/app/collection':
      return !lastLocation || lastLocation === '/app/login'
        ? 'slide'
        : 'inverseslide';
    case '/app/profile':
      return lastLocation === '/app/collection' ? 'slide' : 'inverseslide';
    default:
      return 'slide';
  }
};

const Routes: React.FC = () => {
  const {
    setButtonStyle,
    setMenuStyle,
    setInputStyle,
    setSecondaryBrandColor,
  } = useThemeContext();
  const location = useLocation();
  const { productDetails, desktopView } = useGlobal();
  const lastLocation = useLastLocation();

  const getCurrentTransition = useCallback(() => {
    return getTransition(
      location.pathname,
      lastLocation ? lastLocation.pathname : ''
    );
  }, [location.pathname, lastLocation]);

  const currentTransition = getCurrentTransition();

  const getLocationKey = () => {
    if (location.pathname.includes('/c/')) return `products`;
    return location.key || 'initial';
  };

  // Remounting on key change restarts the CSS animation. Skipped in desktop
  // view (the wide framed product layout) and on the first location, so a
  // cold load or a refresh does not slide in.
  const animationClass =
    !desktopView && lastLocation ? `page-${currentTransition}` : '';

  useEffect(() => {
    if (
      !productDetails ||
      !productDetails.basicTranslationEnabled ||
      localStorage.getItem('default-language') === 'en' ||
      (navigator.language.split('-')[0] === 'en' &&
        !localStorage.getItem('default-language'))
    )
      return;
    localStorage.setItem('translation-refresh-executed', 'false');
    handleTranslation({ productDetails });
  }, [productDetails]);

  useEffect(() => {
    if (!productDetails || !location.pathname.includes('/c/')) return;
    const prefetch = () => {
      import('pages/Login/Login');
      import('pages/Collection/Collection');
      import('pages/Profile/Profile');
      import('pages/GameRules/GameRules');
      import('pages/TermsAndPrivacy/TermsAndPrivacy');
    };
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(prefetch);
    } else {
      setTimeout(prefetch, 3000);
    }
  }, [productDetails, location.pathname]);

  useEffect(() => {
    if (!location.pathname.includes('/c/')) {
      setButtonStyle(CornerStyle.FULL_ROUND);
      setInputStyle(CornerStyle.FULL_ROUND);
      setMenuStyle(CornerStyle.FULL_ROUND);
      setSecondaryBrandColor(theme.secondary);
    }
  }, [
    location.pathname,
    setButtonStyle,
    setSecondaryBrandColor,
    setMenuStyle,
    setInputStyle,
  ]);

  return (
    <div
      key={getLocationKey()}
      className={`absolute inset-0 ${animationClass}`}
    >
      <Suspense fallback={<div className='w-full h-full' />}>
        <Switch location={location}>
          {Object.keys(RoutesHashMap).map((routeKey) => {
            const routeObject = RoutesHashMap[routeKey];
            const path =
              typeof RoutesHashMap[routeKey].path === 'function'
                ? RoutesHashMap[routeKey].path()
                : RoutesHashMap[routeKey].path;
            if (routeObject.protected) {
              return (
                <ProtectedRoute
                  exact
                  path={path}
                  key={routeKey}
                  render={() => <>{routeObject.component}</>}
                />
              );
            }
            // Route everything within the form to open magic link to bottom drawer
            if (location.pathname.includes('/form')) {
              return (
                <Route
                  exact={routeObject.exact}
                  path={path}
                  key={routeKey}
                  render={() => <ProductDetails navToForm={true} />}
                />
              );
            } else {
              return (
                <Route
                  exact={routeObject.exact}
                  path={path}
                  key={routeKey}
                  render={() => <>{routeObject.component}</>}
                />
              );
            }
          })}
          <Route>
            {location?.pathname === '/' ? (
              <Redirect to='/app/login' />
            ) : (
              <Redirect to='/app/404' />
            )}
          </Route>
        </Switch>
      </Suspense>
    </div>
  );
};

export default Routes;
